
// UnsubscribeEmail page handles user unsubscription from emails and provides navigation to preferences
import { Box, Container, Typography, useMediaQuery, useTheme, CssBaseline, styled } from '@mui/material';
import React, { useState, useEffect } from 'react';
import Loader from '../../commons/Loader';
import Header from '../../commons/Header';
import Footer from '../../commons/Footer';
import HDButton from '../../hd-ui-kit/HDButton';
import Api from '../../Api';
import { useSearchParams, useNavigate } from 'react-router-dom';


const UnsubscribeEmail = () => {
  // Theme and responsive helpers
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(false); // Loading state for API call
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.between(819, 1180));
  const navigate = useNavigate();
  let [params] = useSearchParams();
  let uuid = params.get('uuid'); // Unique user identifier from URL

  // Style for main container
  const ContainerStyle = {
    position: 'relative',
    paddingBottom: isMobile || isTablet ? '168px' : 0,
    minHeight: isIpad ? '820px' : '550px',
  };

  // Returns background image URL based on theme and device
  const getBackgroundImg = () => {
    let backgroundImageUrl;
    if (theme.palette.mode === 'light') {
      if (isMobile || isTablet) {
        backgroundImageUrl = 'url(../res/unsubscribeLightMobile.svg)';
      } else {
        backgroundImageUrl = 'url(../res/unsubscribeLight.svg)';
      }
    } else {
      backgroundImageUrl = 'url(../res/unsubscribeDark.svg)';
    }
    return backgroundImageUrl;
  };

  // Styled background image overlay
  const BackImage = styled(Box)(() => ({
    display: 'block',
    position: 'absolute',
    width: '100%',
    height: '100%',
    content: '""',
    backgroundImage: getBackgroundImg(),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: isMobile || isTablet ? 'right bottom' : 'right top',
    right: isMobile || isTablet ? 0 : '-30px',
    top: isMobile || isTablet ? 'auto' : 0,
    bottom: 0,
    backgroundSize: isMobile || isTablet ? '85%' : '45%',
  }));

  // Style for content wrapper (centered box)
  const contentWrapStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    position: 'relative',
    marginTop: isMobile || isTablet ? '30px' : '60px',
    paddingBottom: isMobile || isTablet ? 0 : '50px',
    width: isMobile || isTablet ? '100%' : '38%',
  };

  // Style for main page title
  const pageTitle = {
    fontFamily: 'Aventa',
    fontWeight: 500,
    fontSize: '41px',
    lineHeight: '54px',
    textAlign: 'left',
    color: theme.palette.mode === 'light' ? '#002136' : '#fff',
  };

  // Style for subtitle/note
  const titleNote = {
    fontFamily: 'Avenir',
    fontWeight: 300,
    fontSize: '16px',
    lineHeight: '24px',
    textAlign: 'left',
    color: theme.palette.mode === 'light' ? '#334255' : '#CCCCCC',
    marginBottom: '40px',
  };

  // On mount, call API to unsubscribe user using uuid
  useEffect(() => {
    const emailPref = () => {
      setLoading(true);
      let payload = {
        job_opportunities_reminders_opt_in: false,
        profile_reminders_opt_in: false,
        assessment_reminders_opt_in: false,
      };
      Api.updateEmailPreferences(payload, uuid)
        .then((res) => {
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        });
    };
    if (uuid) {
      emailPref();
    }
  }, [uuid]);

  // Handler for "Email Preferences" button: go to profile if logged in, else to login
  const gotoEmailPreference = () => {
    if (Api.isAuthorized()) {
      navigate('/profile-edit?active=0');
    } else {
      navigate('/login', {
        state: {
          fromEmailPRef: true,
        },
      });
    }
  };


  // --- Render UI ---
  return (
    <>
      <CssBaseline /> {/* Normalize CSS across browsers */}
      {loading && <Loader />} {/* Show loader while API call is in progress */}
      <Header /> {/* App header */}
      <Container maxWidth="lg" sx={ContainerStyle}>
        <BackImage></BackImage> {/* Decorative background image */}
        <Box sx={contentWrapStyle}>
          {/* Main title */}
          <Typography sx={pageTitle}>You have been successfully unsubscribed</Typography>
          {/* Subtitle/note */}
          <Typography sx={titleNote}>
            You will no longer receive product update emails. Go to your email preferences to re-subscribe and manage other emails.
          </Typography>
          {/* Button to go to email preferences */}
          <HDButton text={'Email Preferences'} variant={'outlined'} onClick={gotoEmailPreference} />
        </Box>
      </Container>
      <Footer /> {/* App footer */}
    </>
  );
};

export default UnsubscribeEmail;
